
import React from "react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Trash2 } from "lucide-react";
import { DocumentUploadSection } from "../DocumentUploadSection";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "../schema/documentValidationSchema";
import { DocumentRequirement } from "../hooks/useDocumentRequirements";
import { getDocumentPurposeLabel } from "../utils/documentTypeUtils";
import { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";

interface VerificationDocumentFormProps {
  form: UseFormReturn<FormValues>;
  countries: any[];
  idTypes: IDDocumentType[] | undefined;
  onDocumentTypeChange: (value: string, sectionIndex: number) => void;
  documentsNeeded: DocumentRequirement[];
  sections: any[];
  handleSubmitDocuments: (data: FormValues) => void;
  handleRemoveSection?: (index: number) => void;
  verificationCountry: string;
  residencyStatus: string;
}

export const VerificationDocumentForm: React.FC<
  VerificationDocumentFormProps
> = ({
  form,
  countries,
  idTypes,
  onDocumentTypeChange,
  documentsNeeded,
  sections,
  handleSubmitDocuments,
  handleRemoveSection,
  verificationCountry,
  residencyStatus,
}) => {
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitDocuments)}
        className="space-y-6"
      >
        {sections.map((_, sectionIndex) => {
          const sectionDoc = documentsNeeded[sectionIndex];
          const sectionType = sectionDoc?.type || "document";
          const sectionTitle = getDocumentPurposeLabel(sectionType);
          const description = sectionDoc?.description;

          const documentLabel =
            sectionType === "passport" ? "Citizenship Document" : sectionTitle;

          return (
            <div key={sectionIndex} className="relative">
              {sectionIndex >= documentsNeeded.length &&
                handleRemoveSection && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveSection(sectionIndex)}
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

              <DocumentUploadSection
                title={documentLabel}
                description={description}
                form={form}
                countries={countries}
                idTypes={idTypes || []}
                onCountryChange={(value) =>
                  form.setValue(`sections.${sectionIndex}.country_code`, value)
                }
                onDocumentTypeChange={(value) =>
                  onDocumentTypeChange(value, sectionIndex)
                }
                preset={{
                  countryCode: sectionDoc?.countryCode,
                  locked: sectionDoc?.locked,
                }}
                sectionIndex={sectionIndex}
                documentTypeFilter={sectionDoc?.documentTypeFilter}
                excludeCountries={sectionDoc?.excludeCountries}
              />
            </div>
          );
        })}

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            className="bg-nation-teal hover:bg-nation-teal-dark text-white"
            disabled={!form.formState.isDirty}
          >
            Save Document{sections.length !== 1 ? "s" : ""}
          </Button>
        </div>
      </form>
    </Form>
  );
};
